/// <amd-module name="Test/TestSummary/Store/SummaryViewStore" />
import { IAggregatedRunsCount, IAggregatedTestsCount, ISummaryViewState } from "Report.types";
import { ITestAnalyticsLinkData } from "Test/Analytics/Common/Types";
import { IViewContextData, ViewContext, ViewContextStatus } from "Test/common-util/Common";
import * as CommonUtils from "Test/common-util/Common.Utils";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import {
    ISummaryInitializationData,
    ISummaryInitializationDataForUnifiedPipeline,
    TestResultsReportActionsHub,
} from "Test/TestSummary/Actions/TestResultsReportActionsHub";
import {
    DataType,
    DifferenceType,
    IDifference,
    ISummaryNumberChartProps,
    ValueType,
} from "Test/TestSummary/Components/ResultSummaryDetailsChart";
import * as Resources from "Test/TestSummary/Resources";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { Store } from "VSS/Reflux/Store";

export class SummaryViewStore extends Store {
    constructor(changeEvent: string,
        private pageContext: IVssComponentContext,
        private _actionsHub: TestResultsReportActionsHub,
        private _viewContextData: IViewContextData) {
        super(changeEvent);
        this._actionsHub.initializeTestResultSummary.addListener(this._initializeTestResultSummaryHandler.bind(this));
        this._actionsHub.initializeTestResultSummaryForUnifiedPipeline.addListener(this._initializeTestResultSummaryForUnifiedPipelineHandler.bind(this));
        this._actionsHub.refreshTestResultSummary.addListener(this._refreshSummary.bind(this));
        this._initialize();
    }

    private _initialize(): void {
        this._setDefaultState();
    }

    public getState(): ISummaryViewState {
        return this._state;
    }

    public dispose() {
        this._actionsHub!.initializeTestResultSummary.removeListener(this._initializeTestResultSummaryHandler);
        this._actionsHub.initializeTestResultSummaryForUnifiedPipeline.removeListener(this._initializeTestResultSummaryForUnifiedPipelineHandler);
        this._actionsHub!.refreshTestResultSummary.removeListener(this._refreshSummary);
    }

    public getDefinitionId(): number {
        if (this._viewContextData.viewContext == ViewContext.Build) {
            return this._viewContextData.data.mainData.definition.id;
        } else {
            let releaseDefinitionId = 0;
            if (this._viewContextData &&
                this._viewContextData.data &&
                this._viewContextData.data.subData &&
                this._viewContextData.data.subData.environment &&
                this._viewContextData.data.subData.environment.releaseDefinition) {
                releaseDefinitionId = this._viewContextData.data.subData.environment.releaseDefinition.id;
            }
            return releaseDefinitionId;
        }
    }

    private _setDefaultState() {

        this._state = {
            shouldShowSummary: false,
            totalTestsNumberChartProps: null,
            totalAbortedTestsNumberChartProps: null,
            totalTestsChartProps: null,
            testFailuresChartProps: null,
            abortedTestsChartProps: null,
            passPercentageNumberChartProps: null,
            runDurationNumberChartProps: null,
            notReportedNumberChartProps: null,
            errorMessage: null,
            isInProgressView: null,
            aggregatedTestsCount: null,
            aggregatedRunsCount: null,
            aggregatedRunsOutcomeCount: null,
            refreshSummary: false,
            testAnalyticsLinkData: {} as ITestAnalyticsLinkData,
            onlyNotReportedTests: false,
            summaryAccordianState: false

        };
    }

    private _refreshSummary() {
        this._state.refreshSummary = true;
        this.emitChanged();
    }

    private _initializeTestResultSummary(summaryInitializationData: ISummaryInitializationData) {
        this._state.isInProgressView = summaryInitializationData.viewContextStatus === ViewContextStatus.InProgress;
        if (this._state.isInProgressView) {
            this._initializeTestResultSummaryForInProgressView(summaryInitializationData.summaryData);
        } else {
            this._initializeTestResultSummaryForCompletedView(summaryInitializationData.summaryData);
        }
    }

    private _initializeTestResultSummaryForUnifiedPipeline(summaryInitializationDataForUnifiedPipeline: ISummaryInitializationDataForUnifiedPipeline) {
        this._state.isInProgressView = summaryInitializationDataForUnifiedPipeline.viewContextStatus === ViewContextStatus.InProgress;
        if (this._state.isInProgressView) {
            this._initializeTestResultSummaryForInProgressViewInUnifiedPipeline(summaryInitializationDataForUnifiedPipeline.summaryData);
        } else {
            this._initializeTestResultSummaryForCompletedViewInUnifiedPipeline(summaryInitializationDataForUnifiedPipeline.summaryData);
        }
    }

    private _initializeTestResultSummaryHandler(summaryInitializationData: ISummaryInitializationData) {
        this._initializeTestResultSummary(summaryInitializationData);
        this._state.refreshSummary = false;
        this.emitChanged();
    }

    private _initializeTestResultSummaryForUnifiedPipelineHandler(summaryInitializationData: ISummaryInitializationDataForUnifiedPipeline) {
        this._initializeTestResultSummaryForUnifiedPipeline(summaryInitializationData);
        this._state.refreshSummary = false;
        this.emitChanged();
    }

    private _initializeTestResultSummaryForInProgressViewInUnifiedPipeline(testReport: TCMContracts.PipelineTestMetrics) {
        let aggregatedTestsCount: IAggregatedTestsCount = {
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            notImpactedTests: 0,
            notExecutedTests: 0,
            abortedTests: 0,
            otherTests: 0,
        }

        let aggregatedRunsCount: IAggregatedRunsCount = {
            totalRuns: 0,
            inProgressRuns: 0,
            completedRuns: 0,
            abortedRuns: 0,
        }

        if (!!testReport && !!testReport.runSummary && !!testReport.runSummary.runSummaryByState) {
            const runSummaryByState = testReport.runSummary.runSummaryByState;
            
            // Calculate runsCount
            aggregatedRunsCount.inProgressRuns = runSummaryByState[TCMContracts.TestRunState.InProgress] ? runSummaryByState[TCMContracts.TestRunState.InProgress] : 0;
            aggregatedRunsCount.completedRuns = runSummaryByState[TCMContracts.TestRunState.Completed] ? runSummaryByState[TCMContracts.TestRunState.Completed] : 0;
            aggregatedRunsCount.completedRuns += runSummaryByState[TCMContracts.TestRunState.NeedsInvestigation] ? runSummaryByState[TCMContracts.TestRunState.NeedsInvestigation] : 0;
            aggregatedRunsCount.abortedRuns = runSummaryByState[TCMContracts.TestRunState.Aborted] ? runSummaryByState[TCMContracts.TestRunState.Aborted] : 0;

            aggregatedRunsCount.totalRuns = testReport.runSummary.totalRunsCount;        
        }

        // Calculate test result count by outcome.
        if(!!testReport && !!testReport.resultSummary && !!testReport.resultSummary.resultSummaryByRunState)
        {
            const resultSummaryByRunState = testReport.resultSummary.resultSummaryByRunState;

            // Results are first group by Run State
            for (let state of Object.keys(resultSummaryByRunState)) {

                const resultSummaryByOutcome = resultSummaryByRunState[parseInt(state)];
                if (!!resultSummaryByOutcome) {
                    aggregatedTestsCount.totalTests += resultSummaryByOutcome.totalTestCount;

                    const aggregatedResultDetailsByOutcome = resultSummaryByOutcome.aggregatedResultDetailsByOutcome
                    if(!!aggregatedResultDetailsByOutcome)
                    {
                        for (let outcome of Object.keys(aggregatedResultDetailsByOutcome))
                        {
                            const rbo = aggregatedResultDetailsByOutcome[parseInt(outcome)];
                            switch(rbo.outcome)
                            {
                                case TCMContracts.TestOutcome.Passed:
                                {
                                    aggregatedTestsCount.passedTests += rbo.count;
                                    break;
                                }
                                case TCMContracts.TestOutcome.Failed:
                                {
                                    aggregatedTestsCount.failedTests += rbo.count;
                                    break;
                                }
                                case TCMContracts.TestOutcome.NotImpacted:
                                {
                                    aggregatedTestsCount.notImpactedTests += rbo.count;
                                    break;
                                }
                                case TCMContracts.TestOutcome.NotExecuted:
                                {
                                    aggregatedTestsCount.notExecutedTests += rbo.count;
                                    break;
                                }
                                case TCMContracts.TestOutcome.Aborted:
                                {
                                    aggregatedTestsCount.abortedTests += rbo.count;
                                    break;
                                }
                                default:
                                {
                                    break;
                                }
                            }
                        }
                    }
                }
            }

            aggregatedTestsCount.otherTests = aggregatedTestsCount.totalTests
                                                - aggregatedTestsCount.passedTests
                                                - aggregatedTestsCount.failedTests
                                                - aggregatedTestsCount.notImpactedTests
                                                - aggregatedTestsCount.notExecutedTests
                                                - aggregatedTestsCount.abortedTests;
        }

        this._updateStateForTestResultSummaryForInProgressView(aggregatedTestsCount, aggregatedRunsCount);
    }

    private _initializeTestResultSummaryForInProgressView(testReport: TCMContracts.TestResultSummary) {
        let aggregatedTestsCount: IAggregatedTestsCount = {
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            notImpactedTests: 0,
            notExecutedTests: 0,
            abortedTests: 0,
            otherTests: 0,
        }

        let aggregatedRunsCount: IAggregatedRunsCount = {
            totalRuns: 0,
            inProgressRuns: 0,
            completedRuns: 0,
            abortedRuns: 0,
        }

        if (!!testReport && !!testReport.aggregatedResultsAnalysis && !!testReport.aggregatedResultsAnalysis.runSummaryByState) {
            const runSummaryByState = testReport.aggregatedResultsAnalysis.runSummaryByState;
            // Calculate runsCount
            aggregatedRunsCount.inProgressRuns = runSummaryByState[TCMContracts.TestRunState.InProgress] ? runSummaryByState[TCMContracts.TestRunState.InProgress].runsCount : 0;
            aggregatedRunsCount.completedRuns = runSummaryByState[TCMContracts.TestRunState.Completed] ? runSummaryByState[TCMContracts.TestRunState.Completed].runsCount : 0;
            aggregatedRunsCount.completedRuns += runSummaryByState[TCMContracts.TestRunState.NeedsInvestigation] ? runSummaryByState[TCMContracts.TestRunState.NeedsInvestigation].runsCount : 0;
            aggregatedRunsCount.abortedRuns = runSummaryByState[TCMContracts.TestRunState.Aborted] ? runSummaryByState[TCMContracts.TestRunState.Aborted].runsCount : 0;

            for (let state of Object.keys(runSummaryByState)) {
                aggregatedRunsCount.totalRuns += runSummaryByState[parseInt(state)].runsCount;

                // Calculate outcome counts
                const resultsByOutcome = runSummaryByState[parseInt(state)].resultsByOutcome;
                if (resultsByOutcome) {
                    aggregatedTestsCount.totalTests += this._getAggregatedCount(resultsByOutcome);
                    aggregatedTestsCount.passedTests += resultsByOutcome[TCMContracts.TestOutcome.Passed] ? resultsByOutcome[TCMContracts.TestOutcome.Passed].count : 0;
                    aggregatedTestsCount.failedTests += resultsByOutcome[TCMContracts.TestOutcome.Failed] ? resultsByOutcome[TCMContracts.TestOutcome.Failed].count : 0;
                    aggregatedTestsCount.notImpactedTests += resultsByOutcome[TCMContracts.TestOutcome.NotImpacted] ? resultsByOutcome[TCMContracts.TestOutcome.NotImpacted].count : 0;
                    aggregatedTestsCount.notExecutedTests += resultsByOutcome[TCMContracts.TestOutcome.NotExecuted] ? resultsByOutcome[TCMContracts.TestOutcome.NotExecuted].count : 0;
                    aggregatedTestsCount.abortedTests += resultsByOutcome[TCMContracts.TestOutcome.Aborted] ? resultsByOutcome[TCMContracts.TestOutcome.Aborted].count : 0;
                }
            }

            aggregatedTestsCount.otherTests = aggregatedTestsCount.totalTests
                                                - aggregatedTestsCount.passedTests
                                                - aggregatedTestsCount.failedTests
                                                - aggregatedTestsCount.notImpactedTests
                                                - aggregatedTestsCount.notExecutedTests
                                                - aggregatedTestsCount.abortedTests;
        }

        this._updateStateForTestResultSummaryForInProgressView(aggregatedTestsCount, aggregatedRunsCount);
    }

    private _updateStateForTestResultSummaryForInProgressView(aggregatedTestsCount: IAggregatedTestsCount, aggregatedRunsCount: IAggregatedRunsCount) {
        if (aggregatedRunsCount.totalRuns > 0) {
            this._state.shouldShowSummary = true;
        }

        this._state.aggregatedRunsCount = {
            totalRuns: aggregatedRunsCount.totalRuns,
            inProgressRuns: aggregatedRunsCount.inProgressRuns,
            completedRuns: aggregatedRunsCount.completedRuns,
            abortedRuns: aggregatedRunsCount.abortedRuns
        };

        this._state.aggregatedTestsCount = {
            totalTests: aggregatedTestsCount.totalTests,
            passedTests: aggregatedTestsCount.passedTests,
            failedTests: aggregatedTestsCount.failedTests,
            notImpactedTests: aggregatedTestsCount.notImpactedTests,
            notExecutedTests: aggregatedTestsCount.notExecutedTests,
            abortedTests: aggregatedTestsCount.abortedTests,
            otherTests: aggregatedTestsCount.otherTests
        };
    }

    private _initializeTestResultSummaryForCompletedViewInUnifiedPipeline(testReport: TCMContracts.PipelineTestMetrics) {
        let existingFailures: number = 0;
        let increaseInPassPercentage: number = 0;
        let increaseInRunDuration: string = "";
        let increaseInTotalFailures: number = 0;
        let increaseInTotalTests: number = 0;
        let newFailures: number = 0;
        let notReportedTests: number = 0;
        let otherTests: number = 0;
        let passedOnRerun: number = 0;
        let passPercentage: number = 0;
        let runDuration: string = "";
        let totalTestsWithoutNotImpactedTests: number = 0;
        let totalFailures: number = 0;
        let totalNotImpactedTests: number = 0;
        let totalPassed: number = 0;
        let totalTests: number = 0;
        let passedRunsCount: number = 0;
        let failedRunsCount: number = 0;
        let notImpactedRunsCount: number = 0;
        let othersRunsCount: number = 0;
        let showDifference: boolean = false;
        let shouldShowFailuresChart: boolean = false;

        let testsCountInAbortedRuns: IAggregatedTestsCount = {
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            notImpactedTests: 0,
            notExecutedTests: 0,
            abortedTests: 0,
            otherTests: 0,
        }

        let aggregatedRunsCount: IAggregatedRunsCount = {
            totalRuns: 0,
            inProgressRuns: 0,
            completedRuns: 0,
            abortedRuns: 0
        }

        // Compute result summary
        if (!!testReport && !!testReport.resultSummary) 
        {
            if(!!testReport.resultSummary.resultSummaryByRunState)
            {
                for (let state of Object.keys(testReport.resultSummary.resultSummaryByRunState))
                {
                    const resultSummaryByOutcome = testReport.resultSummary.resultSummaryByRunState[parseInt(state)];
                    if (!!resultSummaryByOutcome && 
                        (parseInt(state) === TCMContracts.TestRunState.Completed || parseInt(state) === TCMContracts.TestRunState.NeedsInvestigation))
                    {
                        // Total test count
                        totalTests += resultSummaryByOutcome.totalTestCount;

                        // Not reported tests count
                        notReportedTests += resultSummaryByOutcome.notReportedTestCount;

                        const aggregatedResultDetailsByOutcome = resultSummaryByOutcome.aggregatedResultDetailsByOutcome
                        if(!!aggregatedResultDetailsByOutcome)
                        {
                            for (let outcome of Object.keys(aggregatedResultDetailsByOutcome))
                            {
                                const aggResultByOutcome = aggregatedResultDetailsByOutcome[parseInt(outcome)];
                                switch(aggResultByOutcome.outcome)
                                {
                                    case TCMContracts.TestOutcome.Passed:
                                    {
                                        totalPassed += aggResultByOutcome.count;
                                        passedOnRerun += aggResultByOutcome.rerunResultCount;
                                        break;
                                    }
                                    case TCMContracts.TestOutcome.Failed:
                                    {
                                        totalFailures += aggResultByOutcome.count;
                                        break;
                                    }
                                    case TCMContracts.TestOutcome.NotImpacted:
                                    {
                                        totalNotImpactedTests += aggResultByOutcome.count;
                                        break;
                                    }
                                    default:
                                    {
                                        otherTests += aggResultByOutcome.count;
                                    }
                                }
                            }
                        }
                    }
                }

                if(totalTests > 0)
                {
                    this._state.shouldShowSummary = true;
                }
                else if(notReportedTests > 0)
                {
                    this._state.shouldShowSummary = true;
                    this._state.onlyNotReportedTests = true;
                    Telemetry.publishEvent(this.pageContext.pageContext, this.pageContext, TelemetryConstants.featureTestTab_TestTabWithOnlyNotReportedTests, {
                        "NotOnlyReportedTestsCounts": notReportedTests
                    });
                }

                // Total tests count include NotImpacted test count which we dont use for pass percentage calculation
                totalTestsWithoutNotImpactedTests = totalTests - totalNotImpactedTests;

                // Pass percentage
                if (totalTestsWithoutNotImpactedTests > 0) {
                    passPercentage = (totalPassed / totalTestsWithoutNotImpactedTests) * 100;
                }
            }
        }

        // Compute run summary
        if (!!testReport  && !!testReport.runSummary && !!testReport.runSummary.runSummaryByState) 
        {
            // Populate Aborted Runs information
            aggregatedRunsCount.abortedRuns = testReport.runSummary.runSummaryByState[TCMContracts.TestRunState.Aborted];
            if (aggregatedRunsCount.abortedRuns > 0 && !!testReport.resultSummary && testReport.resultSummary.resultSummaryByRunState) 
            {
                this._state.shouldShowSummary = true;

                const resultSummaryByOutcome = testReport.resultSummary.resultSummaryByRunState[TCMContracts.TestRunState.Aborted];
                if (!!resultSummaryByOutcome && !!resultSummaryByOutcome.aggregatedResultDetailsByOutcome) 
                {
                    testsCountInAbortedRuns.totalTests = resultSummaryByOutcome.totalTestCount;

                    const aggregatedResultDetailsByOutcome = resultSummaryByOutcome.aggregatedResultDetailsByOutcome
                    for (let outcome of Object.keys(aggregatedResultDetailsByOutcome))
                    {
                        const aggResultByOutcome = aggregatedResultDetailsByOutcome[parseInt(outcome)];
                        switch(aggResultByOutcome.outcome)
                        {
                            case TCMContracts.TestOutcome.Passed:
                            {
                                testsCountInAbortedRuns.passedTests = aggResultByOutcome.count;
                                break;
                            }
                            case TCMContracts.TestOutcome.Failed:
                            {
                                testsCountInAbortedRuns.failedTests = aggResultByOutcome.count;
                                break;
                            }
                            case TCMContracts.TestOutcome.NotImpacted:
                            {
                                testsCountInAbortedRuns.notImpactedTests = aggResultByOutcome.count;
                                break;
                            }
                            case TCMContracts.TestOutcome.Aborted:
                            {
                                testsCountInAbortedRuns.abortedTests = aggResultByOutcome.count;
                                break;
                            }
                            default:
                            {
                                testsCountInAbortedRuns.otherTests += aggResultByOutcome.count
                            }
                        }
                    }
                }

                this._populateStateOfAbortedRuns(testsCountInAbortedRuns);
            }

            // Calculate runs count for completed/NeedInvestigation runs.
            aggregatedRunsCount.completedRuns += testReport.runSummary.runSummaryByState[TCMContracts.TestRunState.Completed] ? testReport.runSummary.runSummaryByState[TCMContracts.TestRunState.Completed] : 0;
            aggregatedRunsCount.completedRuns += testReport.runSummary.runSummaryByState[TCMContracts.TestRunState.NeedsInvestigation] ? testReport.runSummary.runSummaryByState[TCMContracts.TestRunState.NeedsInvestigation] : 0;

            if(aggregatedRunsCount.completedRuns > 0)
            {
                this._state.shouldShowSummary = true;
            }

            // Calculate runs count by outcome for completed/NeedInvestigation runs.
            if (!!testReport.runSummary.runSummaryByOutcome)
            {
                const runSummaryByOutcome = testReport.runSummary.runSummaryByOutcome;
                passedRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.Passed] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.Passed] : 0;
                failedRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.Failed] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.Failed] : 0;
                notImpactedRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.NotImpacted] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.NotImpacted] : 0;
                othersRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.Others] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.Others] : 0;
            }

            // Run duration for completed/NeedInvestigation runs
            if(!!testReport.runSummary.duration)
            {
                runDuration = testReport.runSummary.duration;
            }
        }

        // Compute result analysis with respect to last pipeline intance
        if(!!testReport && !!testReport.resultsAnalysis)
        {
            // Compute failure analysis
            const failureAnalysis = testReport.resultsAnalysis.testFailuresAnalysis;
            if(!!failureAnalysis)
            {
                shouldShowFailuresChart = true;
                newFailures = failureAnalysis.newFailures ? failureAnalysis.newFailures.count : 0;
                existingFailures = failureAnalysis.existingFailures ? failureAnalysis.existingFailures.count : 0;
            }
            else
            {
                existingFailures = totalFailures;
            }

            // Calculate Difference from last pipeline
            const resultsDifference = testReport.resultsAnalysis.resultsDifference;
            if (!!resultsDifference) 
            {
                showDifference = true;
                // Increase in run duration
                increaseInRunDuration = resultsDifference.increaseInDuration;

                // Increase in total tests
                increaseInTotalTests = resultsDifference.increaseInTotalTests;

                // Increase in failures
                increaseInTotalFailures = resultsDifference.increaseInFailures;

                // Populate pass percentage difference
                increaseInPassPercentage = this._getIncreaseInPassPercentageDifference(
                    totalTestsWithoutNotImpactedTests,
                    resultsDifference.increaseInTotalTests - resultsDifference.increaseInNonImpactedTests,
                    totalPassed,
                    resultsDifference.increaseInPassedTests,
                    passPercentage
                );
            }
        }

        // Total tests number chart props
        this._state.totalTestsNumberChartProps = this._getTotalTestsNumberChartProps(totalTests, increaseInTotalTests, showDifference);

        // Pass percentage number chart props
        this._state.passPercentageNumberChartProps = this._getPassPercentageNumberChartProps(passPercentage, increaseInPassPercentage, showDifference);

        // Run duration number chart props
        const parsedRunDuration: string = CommonUtils.TestReportDataParser.parseDuration(runDuration);
        this._state.runDurationNumberChartProps = {
            title: Resources.RunDurationHeading,
            value: parsedRunDuration,
            difference: showDifference ? this._getDurationDifference(increaseInRunDuration) : null,
            infoMessage: Resources.RunDurationInfoMessage
        };

        // Not reported tests number chart props
        this._state.notReportedNumberChartProps = {
            title: Resources.TestsNotReportedHeading,
            value: notReportedTests.toLocaleString(),
            difference: this._getNullDifference()
        };

        // Test Run Outcome
        this._state.aggregatedRunsOutcomeCount = {
            passed: passedRunsCount,
            failed: failedRunsCount,
            notImpacted: notImpactedRunsCount,
            others: othersRunsCount
        };

        // Total tests pie chart data
        this._state.totalTestsChartProps = {
            aggregatedtotalTests: totalTests,
            totalPassed: totalPassed,
            totalPassedOnRerun: passedOnRerun,
            totalFailures: totalFailures,
            totalNotImpactedTests: totalNotImpactedTests,
            otherTests: otherTests
        };

        this._state.testFailuresChartProps =
            shouldShowFailuresChart
                ? {
                    totalFailures: totalFailures,
                    newFailures: newFailures,
                    existingFailures: existingFailures,
                    increaseInFailures: increaseInTotalFailures
                }
                : null;

        // Populate Runs information
        this._state.aggregatedRunsCount = aggregatedRunsCount;
    }

    private _initializeTestResultSummaryForCompletedView(testReport: TCMContracts.TestResultSummary) {
        let aggregatedTotalTests: number = 0;
        let existingFailures: number = 0;
        let increaseInPassPercentage: number = 0;
        let increaseInRunDuration: string = "";
        let increaseInTotalFailures: number = 0;
        let increaseInTotalTests: number = 0;
        let newFailures: number = 0;
        let notReportedTests: number = 0;
        let otherTests: number = 0;
        let passedOnRerun: number = 0;
        let passPercentage: number = 0;
        let runDuration: string = "";
        let totalFailures: number = 0;
        let totalNotImpactedTests: number = 0;
        let totalPassed: number = 0;
        let totalTests: number = 0;
        let passedRunsCount: number = 0;
        let failedRunsCount: number = 0;
        let notImpactedRunsCount: number = 0;
        let othersRunsCount: number = 0;

        let testsCountInAbortedRuns: IAggregatedTestsCount = {
            totalTests: 0,
            passedTests: 0,
            failedTests: 0,
            notImpactedTests: 0,
            notExecutedTests: 0,
            abortedTests: 0,
            otherTests: 0,
        }

        let aggregatedRunsCount: IAggregatedRunsCount = {
            totalRuns: 0,
            inProgressRuns: 0,
            completedRuns: 0,
            abortedRuns: 0
        }

        if (!!testReport && !!testReport.aggregatedResultsAnalysis) {

            const resultsByOutcome = testReport.aggregatedResultsAnalysis.resultsByOutcome;
            const notReportedResultsByOutcome = testReport.aggregatedResultsAnalysis.notReportedResultsByOutcome;

            if (Object.keys(resultsByOutcome).length > 0 ) {
                this._state.shouldShowSummary = true;
            }
            else if(Object.keys(notReportedResultsByOutcome).length > 0) {
                this._state.shouldShowSummary = true;
                this._state.onlyNotReportedTests = true;
                Telemetry.publishEvent(this.pageContext.pageContext, this.pageContext, TelemetryConstants.featureTestTab_TestTabWithOnlyNotReportedTests, {
                    "NotOnlyReportedTestsCounts": Object.keys(notReportedResultsByOutcome).length
                });
            }

            // Total test count
            aggregatedTotalTests = this._getAggregatedCount(resultsByOutcome);
            totalTests = testReport.aggregatedResultsAnalysis.totalTests;

            // Not reported tests count
            notReportedTests = this._getAggregatedCount(testReport.aggregatedResultsAnalysis.notReportedResultsByOutcome);

            // Aborted runs count
            if (!!testReport.aggregatedResultsAnalysis.runSummaryByState &&
                !!testReport.aggregatedResultsAnalysis.runSummaryByState[TCMContracts.TestRunState.Aborted]) {
                this._state.shouldShowSummary = true;
                aggregatedRunsCount.abortedRuns = testReport.aggregatedResultsAnalysis
                    .runSummaryByState[TCMContracts.TestRunState.Aborted].runsCount;
            }

            // Failed tests count
            if (resultsByOutcome && resultsByOutcome[TCMContracts.TestOutcome.Failed]) {
                totalFailures = resultsByOutcome[TCMContracts.TestOutcome.Failed].count;
            }
            // This is defined for newer builds and undefined for older builds (older builds are those which are created before
            // we started calculating run summary and insights for runs of build.This can be 'XAML' or 'VNext' build)
            if (!!testReport.testFailures) {
                const testFailures = CommonUtils.TestReportDataParser.parseFailureData(testReport.testFailures);
                newFailures = testFailures.newFailures;
                existingFailures = testFailures.existingFailures;
            } else {
                existingFailures = totalFailures;
            }

            // Passed tests count
            if (resultsByOutcome && resultsByOutcome[TCMContracts.TestOutcome.Passed]) {
                totalPassed = resultsByOutcome[TCMContracts.TestOutcome.Passed].count;
                if (resultsByOutcome[TCMContracts.TestOutcome.Passed].rerunResultCount) {
                    passedOnRerun = resultsByOutcome[TCMContracts.TestOutcome.Passed].rerunResultCount;
                }
            }

            // Pass percentage
            if (totalTests) {
                passPercentage = (totalPassed / totalTests) * 100;
            }

            // Not impacted tests
            if (!!testReport.aggregatedResultsAnalysis.resultsByOutcome[TCMContracts.TestOutcome.NotImpacted]) {
                totalNotImpactedTests = testReport.aggregatedResultsAnalysis
                    .resultsByOutcome[TCMContracts.TestOutcome.NotImpacted].count;
            }

            // Other tests
            otherTests = aggregatedTotalTests - (totalPassed + totalFailures + totalNotImpactedTests);

            // Run duration and increase in run duration
            runDuration = testReport.aggregatedResultsAnalysis.duration;

            // Calculate Difference
            if (testReport.aggregatedResultsAnalysis.resultsDifference) {
                const resultsDifference = testReport.aggregatedResultsAnalysis.resultsDifference;

                // Increase in total tests
                increaseInTotalTests = resultsDifference.increaseInOtherTests +
                    resultsDifference.increaseInFailures + resultsDifference.increaseInPassedTests;

                // Increase in run duration
                increaseInRunDuration = resultsDifference.increaseInDuration;

                // Increase in failures
                increaseInTotalFailures = resultsDifference.increaseInFailures;

                // Populate pass percentage difference
                if (!!testReport.testFailures) {
                    increaseInPassPercentage = this._getIncreaseInPassPercentageDifference(
                        totalTests,
                        resultsDifference.increaseInTotalTests,
                        totalPassed,
                        resultsDifference.increaseInPassedTests,
                        passPercentage
                    );
                }
            }

            // Calculate completed runs count
            if (!!testReport.aggregatedResultsAnalysis.runSummaryByState) {
                const runSummaryByState = testReport.aggregatedResultsAnalysis.runSummaryByState;
                aggregatedRunsCount.completedRuns = runSummaryByState[TCMContracts.TestRunState.Completed] ? runSummaryByState[TCMContracts.TestRunState.Completed].runsCount : 0;
            }

            // Calculate runs outcome for completed runs
            if (!!testReport.aggregatedResultsAnalysis.runSummaryByOutcome) {
                const runSummaryByOutcome = testReport.aggregatedResultsAnalysis.runSummaryByOutcome;
                passedRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.Passed] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.Passed].runsCount : 0;
                failedRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.Failed] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.Failed].runsCount : 0;
                notImpactedRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.NotImpacted] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.NotImpacted].runsCount : 0;
                othersRunsCount = runSummaryByOutcome[TCMContracts.TestRunOutcome.Others] ? runSummaryByOutcome[TCMContracts.TestRunOutcome.Others].runsCount : 0;
            }
        }

        // Populate state object
        const showDifference: boolean = !!testReport.aggregatedResultsAnalysis.resultsDifference;

        // Total tests number chart props
        this._state.totalTestsNumberChartProps = this._getTotalTestsNumberChartProps(aggregatedTotalTests, increaseInTotalTests, showDifference);

        // Pass percentage number chart props
        this._state.passPercentageNumberChartProps = this._getPassPercentageNumberChartProps(passPercentage, increaseInPassPercentage, showDifference);

        // Run duration number chart props
        const parsedRunDuration: string = CommonUtils.TestReportDataParser.parseDuration(runDuration);
        this._state.runDurationNumberChartProps = {
            title: Resources.RunDurationHeading,
            value: parsedRunDuration,
            difference: showDifference ? this._getDurationDifference(increaseInRunDuration) : null,
            infoMessage: Resources.RunDurationInfoMessage
        };

        // Not reported tests number chart props
        this._state.notReportedNumberChartProps = {
            title: Resources.TestsNotReportedHeading,
            value: notReportedTests.toLocaleString(),
            difference: this._getNullDifference()
        };

        // Test Run Outcome
        this._state.aggregatedRunsOutcomeCount = {
            passed: passedRunsCount,
            failed: failedRunsCount,
            notImpacted: notImpactedRunsCount,
            others: othersRunsCount
        };

        // Total tests pie chart data
        this._state.totalTestsChartProps = {
            aggregatedtotalTests: aggregatedTotalTests,
            totalPassed: totalPassed,
            totalPassedOnRerun: passedOnRerun,
            totalFailures: totalFailures,
            totalNotImpactedTests: totalNotImpactedTests,
            otherTests: otherTests
        };

        // Test failures pie chart data
        const shouldShowFailuresChart: boolean = !!testReport.testFailures;
        this._state.testFailuresChartProps =
            shouldShowFailuresChart
                ? {
                    totalFailures: totalFailures,
                    newFailures: newFailures,
                    existingFailures: existingFailures,
                    increaseInFailures: increaseInTotalFailures
                }
                : null;

        // Populate Runs information
        this._state.aggregatedRunsCount = aggregatedRunsCount;

        // Populate Aborted Runs information
        if (aggregatedRunsCount.abortedRuns > 0) {
            //// Calculate outcome counts
            const resultsByOutcome = testReport.aggregatedResultsAnalysis
                .runSummaryByState[TCMContracts.TestRunState.Aborted].resultsByOutcome;

            if (resultsByOutcome) {
                testsCountInAbortedRuns.totalTests = this._getAggregatedCount(resultsByOutcome);

                testsCountInAbortedRuns.passedTests = resultsByOutcome[TCMContracts.TestOutcome.Passed]
                    ? resultsByOutcome[TCMContracts.TestOutcome.Passed].count
                    : 0;
                testsCountInAbortedRuns.failedTests = resultsByOutcome[TCMContracts.TestOutcome.Failed]
                    ? resultsByOutcome[TCMContracts.TestOutcome.Failed].count
                    : 0;
                testsCountInAbortedRuns.abortedTests = resultsByOutcome[TCMContracts.TestOutcome.Aborted]
                    ? resultsByOutcome[TCMContracts.TestOutcome.Aborted].count
                    : 0;
                testsCountInAbortedRuns.notImpactedTests = resultsByOutcome[TCMContracts.TestOutcome.NotImpacted]
                    ? resultsByOutcome[TCMContracts.TestOutcome.NotImpacted].count
                    : 0;

                otherTests = testsCountInAbortedRuns.totalTests -
                    testsCountInAbortedRuns.passedTests -
                    testsCountInAbortedRuns.failedTests -
                    testsCountInAbortedRuns.notImpactedTests -
                    testsCountInAbortedRuns.abortedTests;
            }

            this._populateStateOfAbortedRuns(testsCountInAbortedRuns);
        }
    }

    private _populateStateOfAbortedRuns(testsCountInAbortedRuns: IAggregatedTestsCount)
    {
        // Total Aborted tests chart props
        this._state.totalAbortedTestsNumberChartProps = this._getTotalAbortedTestsNumberChartProps(testsCountInAbortedRuns.totalTests);

        this._state.abortedTestsChartProps = {
            totalTests: testsCountInAbortedRuns.totalTests,
            totalPassed: testsCountInAbortedRuns.passedTests,
            totalFailures: testsCountInAbortedRuns.failedTests,
            totalAborted: testsCountInAbortedRuns.abortedTests,
            totalNotImpactedTests: testsCountInAbortedRuns.notImpactedTests,
            otherTests: testsCountInAbortedRuns.otherTests
        };
    }

    private _getTotalTestsNumberChartProps(aggregatedTotalTests: number, increaseInTotalTests: number, shouldShowDifference: boolean): ISummaryNumberChartProps {
        return {
            title: Resources.TotalTestsText,
            value: aggregatedTotalTests.toLocaleString(),
            difference: shouldShowDifference ? this._getTotalTestsDifference(increaseInTotalTests) : null,
            dataType: DataType.String
        };
    }

    private _getTotalAbortedTestsNumberChartProps(aggregatedTotalTests: number): ISummaryNumberChartProps {
        return {
            title: Resources.TotalAbortedRunText,
            value: aggregatedTotalTests.toLocaleString(),
            difference: null,
            dataType: DataType.String
        };
    }

    private _getTotalTestsDifference(increaseInTotalTests: number): IDifference {
        let value: string;
        let valueType: ValueType;

        if (increaseInTotalTests < 0) {
            value = Utils_String.localeFormat("{0}", increaseInTotalTests.toLocaleString());
            valueType = ValueType.Decreased;
        } else if (increaseInTotalTests > 0) {
            value = Utils_String.localeFormat("+{0}", increaseInTotalTests.toLocaleString());
            valueType = ValueType.Increased;
        } else {
            value = Utils_String.localeFormat("+{0}", increaseInTotalTests.toLocaleString());
            valueType = ValueType.Unchanged;
        }

        const difference: IDifference = {
            diffType: DifferenceType.Unchanged,
            shouldShowIcon: false,
            value: value,
            valueType: valueType
        };

        return difference;
    }

    private _getPassPercentageNumberChartProps(passPercentage: number, increaseInPassPercentage: number, shouldShowDifference: boolean): ISummaryNumberChartProps {
        const passPercentageValue: string = (passPercentage === Math.round(passPercentage))
            ? passPercentage.toString()
            : CommonUtils.TestReportDataParser.getCustomizedDecimalValueInCurrentLocale(passPercentage, 2);

        const passPercentageDifference: IDifference = this._getPassPercentageDifference(increaseInPassPercentage);

        const passPercentageNumberChartProps: ISummaryNumberChartProps = {
            title: Resources.PassPercentHeading,
            value: passPercentageValue,
            difference: shouldShowDifference ? this._getPassPercentageDifference(increaseInPassPercentage) : null,
            dataType: DataType.Percentage
        };

        return passPercentageNumberChartProps;
    }

    private _getPassPercentageDifference(increaseInPassPercentage: number): IDifference {
        let valueType: ValueType;
        let diffType: DifferenceType;

        const absoluteIncreaseInPassPercentage: number = Math.abs(increaseInPassPercentage);
        const diffValue: string = (absoluteIncreaseInPassPercentage === Math.round(absoluteIncreaseInPassPercentage))
            ? absoluteIncreaseInPassPercentage.toString()
            : CommonUtils.TestReportDataParser.getCustomizedDecimalValueInCurrentLocale(absoluteIncreaseInPassPercentage, 1);
        const value: string = Utils_String.localeFormat("{0}{1}", diffValue, "%");

        if (increaseInPassPercentage < 0) {
            valueType = ValueType.Decreased;
            diffType = DifferenceType.Worsened;
        } else if (increaseInPassPercentage > 0) {
            valueType = ValueType.Increased;
            diffType = DifferenceType.Improved;
        } else {
            valueType = ValueType.Unchanged;
            diffType = DifferenceType.Unchanged;
        }

        const difference: IDifference = {
            diffType: diffType,
            shouldShowIcon: true,
            value: value,
            valueType: valueType
        };

        return difference;
    }

    private _getDurationDifference(durationDifference: string): IDifference {

        let difference: IDifference;
        if (CommonUtils.TestReportDataParser.isZeroDuration(durationDifference)) {
            difference = {
                value: Utils_String.localeFormat("{0}", 0),
                diffType: DifferenceType.Unchanged,
                valueType: ValueType.Unchanged,
                shouldShowIcon: true
            };
        } else if (Utils_String.startsWith(durationDifference, "-")) {
            difference = {
                value: Utils_String.localeFormat("{0}", CommonUtils.TestReportDataParser.parseDuration(durationDifference)),
                diffType: DifferenceType.Improved,
                valueType: ValueType.Decreased,
                shouldShowIcon: true
            };
        } else {
            difference = {
                value: Utils_String.localeFormat("+{0}", CommonUtils.TestReportDataParser.parseDuration(durationDifference)),
                diffType: DifferenceType.Worsened,
                valueType: ValueType.Increased,
                shouldShowIcon: true
            };
        }

        return difference;
    }

    private _getIncreaseInPassPercentageDifference(
        totalTests: number,
        increaseInTotalTests: number,
        totalPassed: number,
        increaseInPassedTests: number,
        currentPassPercent: number): number {

        const prevTotalTests = totalTests - increaseInTotalTests;
        const prevPassedTests = totalPassed - increaseInPassedTests;

        let prevPassPercentage: number = 0;
        if (prevTotalTests !== 0) {
            prevPassPercentage = (prevPassedTests / prevTotalTests) * 100;
        }

        return (currentPassPercent - prevPassPercentage);
    }

    private _getNullDifference(): IDifference {
        const nullDifference: IDifference = {
            value: "",
            diffType: DifferenceType.Unchanged,
            valueType: ValueType.Unchanged,
            shouldShowIcon: false
        };

        return nullDifference;
    }

    private _getAggregatedCount(resultsByOutcome: { [key: number]: TCMContracts.AggregatedResultsByOutcome }): number {
        let retVal: number = 0;
        if (resultsByOutcome) {
            for (const key of Object.keys(resultsByOutcome)) {
                retVal += resultsByOutcome[parseInt(key)].count;
            }
        }

        return retVal;
    }

    private _state: ISummaryViewState;
}
